import { useEffect, useState, type FormEvent } from 'react';
import { Mail, Save, Loader2, AlertCircle, Send, CheckCircle2, Inbox, Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { usePlatformMailer, useSavePlatformMailer, useTestMailer } from '@/hooks/useMailer';
import { ApiError } from '@/lib/api-client';
import { cn } from '@/lib/utils';

export function MailerSettingsPage() {
  const { data, isPending } = usePlatformMailer();
  const save = useSavePlatformMailer();
  const test = useTestMailer();

  const [mode, setMode] = useState<'resend' | 'smtp' | 'disabled'>('disabled');
  const [fromAddress, setFromAddress] = useState('');
  const [fromName, setFromName] = useState('');
  const [replyTo, setReplyTo] = useState('');
  const [resendApiKey, setResendApiKey] = useState('');
  const [smtpHost, setSmtpHost] = useState('');
  const [smtpPort, setSmtpPort] = useState(587);
  const [smtpUser, setSmtpUser] = useState('');
  const [smtpPassword, setSmtpPassword] = useState('');
  const [smtpSecure, setSmtpSecure] = useState(false);
  const [inboundApiKey, setInboundApiKey] = useState('');
  const [inboundSecret, setInboundSecret] = useState('');
  const [webhookUrlCopied, setWebhookUrlCopied] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [testResult, setTestResult] = useState<string | null>(null);

  useEffect(() => {
    if (!data?.mailer) return;
    const m = data.mailer;
    setMode(m.mode);
    setFromAddress(m.from_address ?? '');
    setFromName(m.from_name ?? '');
    setReplyTo(m.reply_to ?? '');
    setSmtpHost(m.smtp_host ?? '');
    setSmtpPort(m.smtp_port ?? 587);
    setSmtpUser(m.smtp_user ?? '');
    setSmtpSecure(m.smtp_secure ?? false);
  }, [data]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaved(false);
    try {
      await save.mutateAsync({
        mode,
        from_address: fromAddress || undefined,
        from_name: fromName,
        reply_to: replyTo,
        resend_api_key: resendApiKey || undefined,
        inbound_api_key: inboundApiKey || undefined,
        inbound_webhook_secret: inboundSecret || undefined,
        smtp_host: smtpHost || undefined,
        smtp_port: smtpPort,
        smtp_user: smtpUser || undefined,
        smtp_password: smtpPassword || undefined,
        smtp_secure: smtpSecure,
      });
      setResendApiKey('');
      setInboundApiKey('');
      setInboundSecret('');
      setSmtpPassword('');
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Kaydedilemedi');
    }
  };

  const onTest = async (e: FormEvent) => {
    e.preventDefault();
    setTestResult(null);
    try {
      await test.mutateAsync(testTo);
      setTestResult('✓ Test e-postası gönderildi. Gelen kutunuzu kontrol edin.');
    } catch (err) {
      setTestResult(`✗ ${err instanceof ApiError ? err.message : 'Gönderilemedi'}`);
    }
  };

  if (isPending || !data) return <div className="text-muted-foreground">Yükleniyor…</div>;
  const s = data.mailer;

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <Mail className="w-5 h-5" /> E-posta Ayarları
        </h1>
        <p className="text-muted-foreground text-sm">
          Transactional e-posta gönderimi. Resend API (HTTPS) veya SMTP sunucusu seçilebilir.
          Secret'lar AES-GCM ile şifrelenerek D1'de saklanır.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" /> Mevcut Durum
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-1.5 text-sm">
          <Row label="Mod" value={s.mode === 'disabled' ? 'Kapalı' : s.mode.toUpperCase()} ok={s.mode !== 'disabled'} />
          <Row label="From" value={s.from_address ?? '—'} ok={!!s.from_address} />
          {s.mode === 'resend' && (
            <Row label="Resend API Key" value={s.resend_key_hint ?? '—'} ok={!!s.resend_key_hint} />
          )}
          {s.mode === 'smtp' && (
            <>
              <Row label="SMTP Host" value={s.smtp_host ?? '—'} ok={!!s.smtp_host} />
              <Row label="SMTP Port" value={String(s.smtp_port ?? '—')} ok={!!s.smtp_port} />
              <Row label="SMTP User" value={s.smtp_user ?? '—'} ok={!!s.smtp_user} />
              <Row label="SMTP Şifre" value={s.smtp_password_set ? '✓ Kayıtlı' : 'Yok'} ok={s.smtp_password_set} />
              <Row label="TLS" value={s.smtp_secure ? 'Implicit (465)' : 'STARTTLS (587)'} ok />
            </>
          )}
          <Row label="Yapılandırılmış" value={s.configured ? 'Evet' : 'Hayır'} ok={s.configured} />
        </CardContent>
      </Card>

      <form onSubmit={onSubmit} className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Backend</CardTitle>
            <CardDescription>Hangi yöntemle mail gönderileceğini seç.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Mod</Label>
              <div className="flex gap-2 flex-wrap">
                {(['resend', 'smtp', 'disabled'] as const).map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setMode(m)}
                    className={cn(
                      'px-4 py-2 rounded-md border text-sm font-medium',
                      mode === m
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'border-input hover:bg-accent',
                    )}
                  >
                    {m === 'resend' ? 'Resend API' : m === 'smtp' ? 'SMTP' : 'Kapalı'}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="from">Gönderici E-posta *</Label>
                <Input
                  id="from"
                  type="email"
                  value={fromAddress}
                  onChange={(e) => setFromAddress(e.target.value)}
                  placeholder="noreply@alan-adiniz.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="fromName">Gönderici Ad</Label>
                <Input
                  id="fromName"
                  value={fromName}
                  onChange={(e) => setFromName(e.target.value)}
                  placeholder="CMS Hub"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="replyTo">Reply-To (opsiyonel)</Label>
              <Input
                id="replyTo"
                type="email"
                value={replyTo}
                onChange={(e) => setReplyTo(e.target.value)}
                placeholder="destek@alan-adiniz.com"
              />
            </div>
          </CardContent>
        </Card>

        {mode === 'resend' && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Resend API</CardTitle>
              <CardDescription>
                <a
                  href="https://resend.com/api-keys"
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary hover:underline"
                >
                  resend.com/api-keys
                </a>{' '}
                üzerinden bir API key oluştur ve domain'ini doğrula.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Label htmlFor="rk">
                  API Key{' '}
                  {s.resend_key_hint && (
                    <span className="text-xs text-muted-foreground">(yenilemek için yeni key yaz)</span>
                  )}
                </Label>
                <Input
                  id="rk"
                  type="password"
                  autoComplete="off"
                  value={resendApiKey}
                  onChange={(e) => setResendApiKey(e.target.value)}
                  placeholder={s.resend_key_hint ? '••••••••' : 're_…'}
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Inbound webhook — separate secret from the send API key.
            Always shown (not gated on mode) because inbound works
            independently of outgoing mail mode. */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Inbox className="w-4 h-4" /> Resend Inbound Webhook
            </CardTitle>
            <CardDescription>
              Domain'ine gelen e-postaları{' '}
              <a
                href="/platform/inbox"
                className="text-primary hover:underline"
              >
                Gelen Kutusu
              </a>
              'nda toplar. Resend panelinden ayrı bir webhook secret alınır (gönderim API key'den farklı).{' '}
              <a
                href="https://resend.com/docs/dashboard/receiving/custom-domains"
                target="_blank"
                rel="noreferrer"
                className="text-primary hover:underline"
              >
                Kurulum rehberi →
              </a>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Webhook URL (Resend paneline yapıştır)</Label>
              <div className="flex items-center gap-2">
                <Input
                  readOnly
                  value={`${window.location.origin}/api/webhooks/resend/inbound`}
                  onFocus={(e) => e.currentTarget.select()}
                  className="font-mono text-xs"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    await navigator.clipboard.writeText(
                      `${window.location.origin}/api/webhooks/resend/inbound`,
                    );
                    setWebhookUrlCopied(true);
                    setTimeout(() => setWebhookUrlCopied(false), 2000);
                  }}
                >
                  {webhookUrlCopied ? (
                    <Check className="w-3.5 h-3.5" />
                  ) : (
                    <Copy className="w-3.5 h-3.5" />
                  )}
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="iak">
                Inbound API Key{' '}
                {s.inbound_api_key_hint && (
                  <span className="text-xs text-muted-foreground">(yenilemek için yeni key yaz)</span>
                )}
              </Label>
              <Input
                id="iak"
                type="password"
                autoComplete="off"
                value={inboundApiKey}
                onChange={(e) => setInboundApiKey(e.target.value)}
                placeholder={s.inbound_api_key_hint ? '••••••••' : 're_…'}
              />
              <p className="text-xs text-muted-foreground">
                Gönderim API key'inden <strong>ayrı</strong> bir Resend API key. Resend panel → API Keys → yeni key oluştur,
                <strong> Inbound (read)</strong> iznini seç. Gelen e-postaların gövdesini{' '}
                <code className="text-xs bg-muted px-1 py-0.5 rounded">GET /emails/receiving/:id</code>
                {' '}ile çekmek için kullanılır.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="iws">
                Inbound Webhook Secret{' '}
                {s.inbound_webhook_secret_set && (
                  <span className="text-xs text-muted-foreground">(yenilemek için yeni secret yaz)</span>
                )}
              </Label>
              <Input
                id="iws"
                type="password"
                autoComplete="off"
                value={inboundSecret}
                onChange={(e) => setInboundSecret(e.target.value)}
                placeholder={s.inbound_webhook_secret_set ? '••••••••' : 'whsec_…'}
              />
              <p className="text-xs text-muted-foreground">
                Resend panelinde webhook oluşturulurken verilen{' '}
                <code className="text-xs bg-muted px-1 py-0.5 rounded">whsec_…</code> değerini buraya yapıştır.
                Gelen webhook imzasını doğrulamak için kullanılır.
              </p>
            </div>

            <div className="flex items-center gap-2 text-xs flex-wrap">
              <StatusPill ok={!!s.inbound_api_key_hint}>
                {s.inbound_api_key_hint ? `✓ API Key: ${s.inbound_api_key_hint}` : '○ API Key yok'}
              </StatusPill>
              <StatusPill ok={s.inbound_webhook_secret_set}>
                {s.inbound_webhook_secret_set ? '✓ Webhook Secret kayıtlı' : '○ Webhook Secret yok'}
              </StatusPill>
            </div>
          </CardContent>
        </Card>

        {mode === 'smtp' && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">SMTP Sunucusu</CardTitle>
              <CardDescription>
                Cloudflare Workers SMTP'yi TCP üzerinden destekler. Resend için:
                host <code className="text-xs bg-muted px-1 py-0.5 rounded">smtp.resend.com</code>, port 587.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="host">Host</Label>
                  <Input
                    id="host"
                    value={smtpHost}
                    onChange={(e) => setSmtpHost(e.target.value)}
                    placeholder="smtp.resend.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="port">Port</Label>
                  <Input
                    id="port"
                    type="number"
                    value={smtpPort}
                    onChange={(e) => setSmtpPort(parseInt(e.target.value, 10) || 587)}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="user">Kullanıcı</Label>
                <Input
                  id="user"
                  value={smtpUser}
                  onChange={(e) => setSmtpUser(e.target.value)}
                  placeholder="resend"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pass">
                  Şifre{' '}
                  {s.smtp_password_set && (
                    <span className="text-xs text-muted-foreground">(yenilemek için yeni şifre yaz)</span>
                  )}
                </Label>
                <Input
                  id="pass"
                  type="password"
                  autoComplete="off"
                  value={smtpPassword}
                  onChange={(e) => setSmtpPassword(e.target.value)}
                  placeholder={s.smtp_password_set ? '••••••••' : ''}
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  id="secure"
                  type="checkbox"
                  checked={smtpSecure}
                  onChange={(e) => setSmtpSecure(e.target.checked)}
                  className="h-4 w-4 rounded border-input"
                />
                <Label htmlFor="secure" className="cursor-pointer text-sm">
                  Implicit TLS (port 465). Kapalıysa STARTTLS (port 587) kullanılır.
                </Label>
              </div>
            </CardContent>
          </Card>
        )}

        {error && (
          <div className="text-sm text-destructive bg-destructive/10 rounded-md border border-destructive/30 px-3 py-2 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            {error}
          </div>
        )}
        {saved && (
          <div className="text-sm text-emerald-700 dark:text-emerald-400 bg-emerald-500/10 rounded-md px-3 py-2">
            Kaydedildi ✓
          </div>
        )}
        <Button type="submit" disabled={save.isPending}>
          {save.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Kaydet
        </Button>
      </form>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Send className="w-4 h-4" /> Test E-postası
          </CardTitle>
          <CardDescription>Ayarların çalıştığını doğrulamak için test gönder.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onTest} className="flex gap-2 items-end">
            <div className="space-y-2 flex-1">
              <Label htmlFor="tt">Alıcı e-posta</Label>
              <Input
                id="tt"
                type="email"
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
                placeholder="test@ornek.com"
              />
            </div>
            <Button type="submit" variant="outline" disabled={test.isPending || !testTo || !s.configured}>
              {test.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Gönder'}
            </Button>
          </form>
          {testResult && (
            <div
              className={cn(
                'mt-3 text-sm rounded-md px-3 py-2',
                testResult.startsWith('✓')
                  ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
                  : 'bg-destructive/10 text-destructive',
              )}
            >
              {testResult}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function StatusPill({ ok, children }: { ok: boolean; children: React.ReactNode }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-medium',
        ok
          ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
          : 'bg-muted text-muted-foreground',
      )}
    >
      {children}
    </span>
  );
}

function Row({ label, value, ok }: { label: string; value: string; ok?: boolean }) {
  return (
    <div className="flex items-center justify-between border-b last:border-b-0 py-1.5">
      <span className="text-muted-foreground">{label}</span>
      <span className={cn('font-mono', ok ? 'text-foreground' : 'text-muted-foreground')}>{value}</span>
    </div>
  );
}
